import { forwardRef, useEffect, useState } from 'react';
import { formatDate } from '@/utils/dateFormat';
import html2pdf from 'html2pdf.js';
import QRCode from 'qrcode';

// Helper function to convert number to words (Indian format)
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  if (num === 0) return 'Zero';
  
  const convertLessThanThousand = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
  };
  
  const convert = (n) => {
    if (n < 1000) return convertLessThanThousand(n);
    if (n < 100000) return convertLessThanThousand(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convertLessThanThousand(n % 1000) : '');
    if (n < 10000000) return convertLessThanThousand(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convertLessThanThousand(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };
  
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  
  let result = 'Rupees ' + convert(rupees);
  if (paise > 0) {
    result += ' and ' + convert(paise) + ' Paise';
  }
  result += ' Only';
  
  return result;
};

// Format currency with 2 decimal places
const formatCurrency = (amount) => {
  return (amount || 0).toLocaleString('en-IN', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

const Receipt = forwardRef(({ sale, storeInfo, onPrint, onExportPdf }, ref) => {
  // Generate QR as a local base64 data URL so printing works offline and reprints
  // never miss the QR waiting on api.qrserver.com.
  const websiteUrl = 'https://www.soonyoung.in';
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(websiteUrl, { margin: 1, width: 200, errorCorrectionLevel: 'M' })
      .then((url) => { if (!cancelled) setQrCodeUrl(url); })
      .catch(() => { if (!cancelled) setQrCodeUrl(''); });
    return () => { cancelled = true; };
  }, [websiteUrl]);

  // Export to PDF function - defined before any early returns
  const exportToPdf = () => {
    const element = ref?.current;
    if (!element || !sale) return;
    
    const opt = {
      margin: 2,
      filename: `Bill_${sale.bill_number}_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: [80, 297], orientation: 'portrait' }
    };
    
    html2pdf().set(opt).from(element).save();
    if (onExportPdf) onExportPdf();
  };

  if (!sale) return null;

  const totalQty = sale.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  
  // Calculate tax summary by tax group/rate
  const taxSummary = {};
  // Totals used for the highlighted MRP/Discount/Savings block
  let grossMrpTotal = 0;       // Sum of (MRP x Qty) across all items
  let netLineTotal = 0;        // Sum of final line amount paid (after offer, GST-inclusive)
  
  sale.items?.forEach(item => {
    const rate = item.tax_rate || 0;
    const groupName = item.tax_group_name || (rate > 0 ? `GST ${rate}%` : 'Exempt');
    
    if (!taxSummary[groupName]) {
      taxSummary[groupName] = { 
        rate, 
        taxable: 0, 
        cgst: 0, 
        sgst: 0, 
        igst: 0, 
        total: 0,
        cgstRate: item.cgst_rate || rate / 2,
        sgstRate: item.sgst_rate || rate / 2
      };
    }
    taxSummary[groupName].taxable += item.taxable_amount || 0;
    taxSummary[groupName].cgst += item.cgst_amount || 0;
    taxSummary[groupName].sgst += item.sgst_amount || 0;
    taxSummary[groupName].igst += item.igst_amount || 0;
    taxSummary[groupName].total += item.tax_amount || 0;

    const qty = Number(item.quantity || 0);
    const mrp = Number(item.mrp || item.unit_price || 0);
    const lineAmount = item.total_amount != null
      ? Number(item.total_amount)
      : Number(item.unit_price || 0) * qty;
    grossMrpTotal += mrp * qty;
    netLineTotal += lineAmount;
  });

  const totalLineDiscount = Math.max(0, grossMrpTotal - netLineTotal); // MRP markdown + line offer
  const billDiscountAmount = sale.bill_discount?.amount || 0;
  const totalSavings = totalLineDiscount + billDiscountAmount;
  
  const hasIGST = sale.total_igst > 0;

  const defaultLogoUrl = 'https://customer-assets.emergentagent.com/job_retail-manager-114/artifacts/nycnad0u_output-onlinepngtools.png';
  const logoUrl = storeInfo?.logo_url || defaultLogoUrl;

  // Get bill terms from store settings
  const billTerms = storeInfo?.bill_terms || '';

  // Customer display - show "Walk-in Customer" if no customer info
  const customerName = sale.customer_name || 'Walk-in Customer';
  const customerPhone = sale.customer_phone || '';

  return (
    <div ref={ref} className="receipt-container" data-testid="receipt-container">
      {/* Store Header */}
      <div className="receipt-header">
        {logoUrl ? (
          <div className="logo-container">
            <img 
              src={logoUrl} 
              alt={storeInfo?.name || 'SOON YOUNG'} 
              className="logo-img"
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="logo-text-fallback">{storeInfo?.name || 'SOON YOUNG'}</div>
          </div>
        ) : (
          <div className="store-name">{storeInfo?.name || 'SOON YOUNG'}</div>
        )}
        {/* Store Name under Logo */}
        <div className="store-name-under-logo">{storeInfo?.name || 'SOON YOUNG SAIDAPET'}</div>
        {storeInfo?.address && (
          <div className="store-address">
            {typeof storeInfo.address === 'object' 
              ? `${storeInfo.address.address_line1 || ''}, ${storeInfo.address.city || ''} - ${storeInfo.address.pincode || ''}`
              : storeInfo.address
            }
          </div>
        )}
        {sale.store_gstin && (
          <div className="store-gstin">GSTIN: {sale.store_gstin}</div>
        )}
        {storeInfo?.contact?.phone && (
          <div className="store-phone">Ph: {storeInfo.contact.phone}</div>
        )}
      </div>

      {/* Bill Info */}
      <div className="bill-info section">
        <div className="row">
          <span className="label">Bill No:</span>
          <span className="value bold">{sale.bill_number}</span>
        </div>
        <div className="row">
          <span className="label">Date:</span>
          <span className="value">{formatDate(sale.bill_datetime)}</span>
        </div>
        <div className="row">
          <span className="label">Cashier:</span>
          <span className="value">{sale.cashier_name}</span>
        </div>
      </div>

      {/* Customer Info - Always show */}
      <div className="customer-info section">
        <div className="row">
          <span className="label">Customer:</span>
          <span className="value">{customerName}</span>
        </div>
        {customerPhone && (
          <div className="row">
            <span className="label">Mobile:</span>
            <span className="value">{customerPhone}</span>
          </div>
        )}
        {sale.customer_gstin && (
          <div className="row">
            <span className="label">GSTIN:</span>
            <span className="value">{sale.customer_gstin}</span>
          </div>
        )}
      </div>

      {/* Products Info Section */}
      <div className="products-section">
        <div className="products-header">PRODUCTS INFO</div>
        <div className="products-list">
          {sale.items?.map((item, idx) => {
            const mrp = item.mrp || item.unit_price;
            const qty = Number(item.quantity || 0);
            const gstPercent = item.tax_rate || 0;
            const hsnCode = item.hsn_code ? String(item.hsn_code).padStart(6, '0') : '';

            // Effective per-unit Rate paid by the customer, after any applied
            // offer. We derive it from the line's final total so it stays
            // consistent with what was actually billed.
            const effectiveRate = qty > 0
              ? ((item.total_amount != null ? item.total_amount : (item.unit_price || 0) * qty) / qty)
              : (item.unit_price || 0);
            const lineAmount = effectiveRate * qty;

            // Offer-level info (applied promo/coupon). Dis% = (MRP - Rate)/MRP
            // which naturally caps at (MRP - SP)/MRP when the SP ceiling kicks in.
            const offerDiscountAmount = Math.max(0, (mrp - effectiveRate) * qty);
            const hasOffer = !!(item.applied_offer_id || item.offer_id || item.applied_offer_code || item.offer_code)
                              && offerDiscountAmount > 0;
            let offerPercent = 0;
            if (mrp > 0) {
              offerPercent = ((mrp - effectiveRate) / mrp) * 100;
            }

            return (
              <div key={idx} className="product-block">
                {/* Row 1: Product Name */}
                <div className="product-name">{idx + 1}. {item.product_name}</div>

                {/* Row 2: HSN  |  MRP */}
                <div className="product-row-2">
                  <span className="pr-hsn">HSN: {hsnCode || '--'}</span>
                  <span className="pr-mrp">MRP: {Number(mrp || 0).toFixed(2)}</span>
                </div>

                {/* Row 3: GST  |  Dis (applied offer % off MRP) */}
                <div className="product-row-2 product-row-2b">
                  <span className="pr-gst">GST: {gstPercent}%</span>
                  {hasOffer ? (
                    <span className="pr-dis">Dis: {offerPercent.toFixed(0)}%</span>
                  ) : (
                    <span className="pr-dis-empty">&nbsp;</span>
                  )}
                </div>

                {/* Row 4: Qty | Rate | Amt */}
                <div className="product-row-3">
                  <span className="pr-qty">Qty: {qty}</span>
                  <span className="pr-rate">Rate: {Number(effectiveRate || 0).toFixed(2)}</span>
                  <span className="pr-amt">Amt: {formatCurrency(lineAmount)}</span>
                </div>

                {/* Row 5: Offer + Savings (only when an offer/promo is applied) */}
                {hasOffer && (
                  <div className="product-offer-box">
                    <span className="po-offer">OFFER {offerPercent.toFixed(0)}%</span>
                    <span className="po-sep">&nbsp;&amp;&nbsp;</span>
                    <span className="po-savings">Savings Rs.{formatCurrency(offerDiscountAmount)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Totals Section */}
      <div className="totals-section section">
        <div className="row">
          <span>Items: {sale.items?.length || 0}</span>
          <span>Qty: {totalQty}</span>
        </div>
        <div className="row">
          <span className="label">Subtotal:</span>
          <span className="value bold">Rs.{formatCurrency(sale.subtotal)}</span>
        </div>
        {billDiscountAmount > 0 && (
          <div className="row discount-row">
            <span className="label">Bill Disc ({sale.bill_discount?.value}{sale.bill_discount?.type === 'percent' ? '%' : ''}):</span>
            <span className="value bold">-Rs.{formatCurrency(billDiscountAmount)}</span>
          </div>
        )}
        <div className="row">
          <span className="label">GST (incl.):</span>
          <span className="value">Rs.{formatCurrency(sale.total_tax)}</span>
        </div>
        {sale.round_off !== 0 && (
          <div className="row">
            <span className="label">Round Off:</span>
            <span className="value">{sale.round_off > 0 ? '+' : ''}Rs.{sale.round_off?.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Grand Total */}
      <div className="grand-total-box">
        <div className="grand-total-label">NET TOTAL</div>
        <div className="grand-total-value">Rs. {formatCurrency(sale.grand_total)}</div>
        <div className="amount-words">{numberToWords(sale.grand_total)}</div>
      </div>

      {/* Highlighted MRP / Discount / Savings block */}
      {grossMrpTotal > 0 && (
        <div className="mrp-summary-box">
          <div className="mrp-row">
            <span className="mrp-label">Total MRP</span>
            <span className="mrp-value">Rs. {formatCurrency(grossMrpTotal)}</span>
          </div>
          <div className="mrp-row">
            <span className="mrp-label">Total Discount</span>
            <span className="mrp-value">- Rs. {formatCurrency(totalLineDiscount)}</span>
          </div>
          {billDiscountAmount > 0 && (
            <div className="mrp-row">
              <span className="mrp-label">Bill Discount</span>
              <span className="mrp-value">- Rs. {formatCurrency(billDiscountAmount)}</span>
            </div>
          )}
          <div className="mrp-row mrp-row-savings">
            <span className="mrp-label">Total Savings</span>
            <span className="mrp-value">Rs. {formatCurrency(totalSavings)}</span>
          </div>
        </div>
      )}

      {/* Savings Box */}
      {totalSavings > 0 && (
        <div className="savings-box">
          <span className="savings-star">★</span>
          <span className="savings-label">YOUR SAVINGS:</span>
          <span className="savings-value">Rs. {formatCurrency(totalSavings)}</span>
          <span className="savings-star">★</span>
        </div>
      )}

      {/* Payment Section */}
      <div className="payment-section section">
        <div className="section-title">PAYMENT</div>
        {sale.payments?.map((pay, idx) => (
          <div key={idx} className="row">
            <span className="label uppercase">{pay.mode}:</span>
            <span className="value amount-value">Rs.{formatCurrency(pay.amount)}</span>
          </div>
        ))}
        {sale.change_amount > 0 && (
          <div className="row change-row">
            <span className="label bold">CHANGE:</span>
            <span className="value bold amount-value">Rs.{formatCurrency(sale.change_amount)}</span>
          </div>
        )}
      </div>

      {/* Tax Summary */}
      {Object.keys(taxSummary).length > 0 && (
        <div className="tax-summary section">
          <div className="section-title">TAX SUMMARY</div>
          <table className="tax-table">
            <thead>
              <tr>
                <th>Tax</th>
                <th>Taxable</th>
                <th>{hasIGST ? 'IGST' : 'CGST'}</th>
                {!hasIGST && <th>SGST</th>}
              </tr>
            </thead>
            <tbody>
              {Object.entries(taxSummary).map(([groupName, data]) => (
                <tr key={groupName}>
                  <td>{groupName}</td>
                  <td>{data.taxable.toFixed(2)}</td>
                  <td>{hasIGST ? data.igst.toFixed(2) : data.cgst.toFixed(2)}</td>
                  {!hasIGST && <td>{data.sgst.toFixed(2)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      <div className="receipt-footer">
        <div className="thank-you">Thank You!</div>
        <div className="visit-again">Visit Again</div>
        
        <div className="online-section">
          <div className="online-label">Shop Online</div>
          <div className="online-url">www.soonyoung.in</div>
          <div className="qr-container">
            {qrCodeUrl && <img src={qrCodeUrl} alt="QR Code" className="qr-code" />}
          </div>
          <div className="qr-hint">Scan to visit</div>
        </div>

        {/* Bill Terms & Conditions from Store Settings */}
        {billTerms && (
          <div className="bill-terms">
            <div className="terms-title">TERMS & CONDITIONS</div>
            <div className="terms-content">{billTerms}</div>
          </div>
        )}

        {sale.is_gst_bill && (
          <div className="gst-notice">★ GST Tax Invoice ★</div>
        )}
      </div>

      {/* Action Buttons (hidden in print) */}
      <div className="print-hide action-buttons">
        <div className="print-hint">Press Enter to Print</div>
        <button onClick={exportToPdf} className="pdf-btn" data-testid="export-pdf-btn">
          Export PDF
        </button>
      </div>

      {/* Receipt Styles - ALL BOLD for Thermal Printing */}
      <style>{`
        .receipt-container {
          width: 300px;
          max-width: 300px;
          padding: 12px;
          font-family: 'Segoe UI', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;
          font-size: 14px;
          line-height: 1.45;
          background: white;
          color: black;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        
        /* Header - Centered */
        .receipt-header {
          text-align: center;
          border-bottom: 3px double black;
          padding-bottom: 10px;
          margin-bottom: 8px;
        }
        .logo-container { 
          margin-bottom: 4px; 
          text-align: center;
        }
        .logo-img { 
          height: 50px; 
          max-width: 180px; 
          object-fit: contain;
          display: block;
          margin: 0 auto;
          /* Thermal printers render grey as nearly invisible. Force logo to pitch black. */
          filter: brightness(0) contrast(10000%) saturate(0);
          image-rendering: crisp-edges;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .logo-text-fallback { display: none; font-size: 24px; font-weight: 900; letter-spacing: 3px; }
        .store-name { font-size: 24px; font-weight: 900; letter-spacing: 3px; margin-bottom: 6px; text-align: center; }
        .store-name-under-logo { font-size: 16px; font-weight: 900; margin-bottom: 4px; letter-spacing: 1px; text-align: center; }
        .store-address { font-size: 13px; font-weight: 700; text-align: center; }
        .store-gstin { font-size: 13px; font-weight: 900; margin-top: 2px; text-align: center; }
        .store-phone { font-size: 13px; font-weight: 700; text-align: center; }
        
        /* Sections */
        .section {
          border-bottom: 2px solid black;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .section-title {
          font-size: 13px;
          font-weight: 900;
          margin-bottom: 4px;
        }
        
        /* Rows */
        .row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 2px;
          font-size: 13px;
        }
        .label { font-weight: 700; }
        .value { font-weight: 700; }
        .bold { font-weight: 900 !important; }
        .uppercase { text-transform: uppercase; }
        
        /* Amount values - darker and bigger */
        .amount-value {
          font-weight: 900 !important;
          font-size: 15px !important;
        }
        
        /* ===== Products Info Section ===== */
        .products-section {
          border-bottom: 2px solid black;
          margin-bottom: 8px;
          padding-bottom: 6px;
        }
        .products-header {
          text-align: center;
          font-size: 14px;
          font-weight: 900;
          letter-spacing: 2px;
          padding: 6px 0;
          border-bottom: 2px solid black;
          margin-bottom: 6px;
        }
        .products-list { }
        .product-block {
          padding: 6px 0;
          border-bottom: 1px dashed #444;
        }
        .product-block:last-child { border-bottom: none; }

        .product-name {
          font-size: 15px;
          font-weight: 900;
          line-height: 1.3;
          margin-bottom: 3px;
          word-break: break-word;
        }
        .product-row-2 {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 2px;
        }
        .product-row-2 .pr-dis { font-weight: 900; }
        .product-row-2 .pr-gst { font-weight: 900; }
        .product-row-3 {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
          font-weight: 800;
          margin-bottom: 4px;
        }
        .product-row-3 .pr-qty { flex: 0 0 auto; }
        .product-row-3 .pr-rate { flex: 1 1 auto; text-align: center; }
        .product-row-3 .pr-amt { flex: 0 0 auto; text-align: right; font-weight: 900; }

        .product-offer-box {
          display: inline-block;
          margin-top: 2px;
          padding: 3px 8px;
          border: 1.5px dashed black;
          font-size: 12px;
          font-weight: 900;
          letter-spacing: 0.3px;
        }
        .product-offer-box .po-offer { font-weight: 900; }
        .product-offer-box .po-savings { font-weight: 900; }
        
        /* Totals */
        .totals-section { font-size: 13px; font-weight: 700; }
        .discount-row { font-weight: 900; }
        
        /* Grand Total - BIGGER and DARKER */
        .grand-total-box {
          border: 3px solid black;
          padding: 10px;
          margin-bottom: 8px;
          text-align: center;
        }
        .grand-total-label {
          font-size: 15px;
          font-weight: 900;
          margin-bottom: 4px;
        }
        .grand-total-value {
          font-size: 26px;
          font-weight: 900;
          letter-spacing: 1px;
        }
        .amount-words {
          font-size: 11px;
          font-style: italic;
          font-weight: 700;
          margin-top: 4px;
        }
        
        /* MRP / Discount / Savings highlighted block */
        .mrp-summary-box {
          border: 2px dashed black;
          padding: 8px 10px;
          margin-bottom: 8px;
          background: #f8f8f8;
        }
        .mrp-row {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          font-weight: 800;
          padding: 2px 0;
        }
        .mrp-row-savings {
          border-top: 1.5px solid black;
          margin-top: 4px;
          padding-top: 5px;
          font-size: 14px;
          font-weight: 900;
        }
        .mrp-label { font-weight: 800; }
        .mrp-value { font-weight: 900; }

        /* Savings */
        .savings-box {
          border: 2px solid black;
          padding: 8px;
          margin-bottom: 8px;
          text-align: center;
          font-size: 15px;
          font-weight: 900;
        }
        .savings-star { margin: 0 4px; }
        .savings-value { font-weight: 900; }
        
        /* Payment */
        .change-row {
          border-top: 1px solid black;
          padding-top: 4px;
          margin-top: 4px;
        }
        
        /* Tax Table */
        .tax-table {
          width: 100%;
          font-size: 10px;
          font-weight: 700;
          border-collapse: collapse;
        }
        .tax-table th, .tax-table td {
          padding: 2px 3px;
          text-align: right;
        }
        .tax-table th:first-child, .tax-table td:first-child {
          text-align: left;
        }
        .tax-table th {
          border-bottom: 1px solid black;
          font-weight: 900;
        }
        
        /* Footer */
        .receipt-footer {
          text-align: center;
          margin-top: 10px;
        }
        .thank-you {
          font-size: 16px;
          font-weight: 900;
        }
        .visit-again {
          font-size: 12px;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .online-section {
          border-top: 2px dashed black;
          padding-top: 8px;
          margin-top: 8px;
          text-align: center;
        }
        .online-label { font-size: 11px; font-weight: 700; }
        .online-url { font-size: 13px; font-weight: 800; margin-bottom: 6px; }
        
        /* QR Code - Centered */
        .qr-container {
          text-align: center;
          margin: 8px 0;
        }
        .qr-code { 
          width: 100px; 
          height: 100px;
          display: inline-block;
        }
        .qr-hint { font-size: 10px; font-weight: 700; margin-top: 4px; }
        
        /* Bill Terms & Conditions - Larger Font */
        .bill-terms {
          border-top: 2px dashed black;
          padding-top: 8px;
          margin-top: 8px;
          text-align: left;
        }
        .terms-title {
          font-size: 14px;
          font-weight: 900;
          text-align: center;
          margin-bottom: 6px;
          letter-spacing: 1px;
        }
        .terms-content {
          font-size: 13px;
          font-weight: 700;
          line-height: 1.5;
          white-space: pre-wrap;
        }
        
        .gst-notice {
          font-size: 10px;
          font-weight: 700;
          margin-top: 8px;
          padding-top: 6px;
          border-top: 1px solid black;
        }
        
        /* Action Buttons */
        .action-buttons {
          text-align: center;
          margin-top: 12px;
        }
        .print-hint {
          font-size: 10px;
          color: #999;
          margin-bottom: 8px;
        }
        .pdf-btn {
          padding: 8px 16px;
          background: #3b82f6;
          color: white;
          font-size: 12px;
          font-weight: 700;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
        .pdf-btn:hover { background: #2563eb; }
        
        /* Print Styles for Thermal Printer - ALL BOLD */
        @media print {
          .receipt-container {
            width: 72mm !important;
            max-width: 72mm !important;
            padding: 0 !important;
            padding-left: 2mm !important;
            padding-right: 2mm !important;
            padding-top: 2mm !important;
            padding-bottom: 2mm !important;
            margin: 0 !important;
            box-shadow: none !important;
            font-size: 13px !important;
            font-weight: 700 !important;
            font-family: 'Segoe UI', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif !important;
          }
          .receipt-container * {
            color: black !important;
            background: transparent !important;
            font-weight: 700 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Centered elements in print */
          .receipt-header,
          .logo-container,
          .logo-img,
          .store-name,
          .store-name-under-logo,
          .store-address,
          .store-gstin,
          .store-phone,
          .qr-container,
          .qr-code {
            text-align: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          .logo-img {
            display: block !important;
            margin: 0 auto !important;
          }
          .qr-code {
            display: block !important;
            margin: 0 auto !important;
          }
          
          .receipt-container .product-name,
          .receipt-container .store-name,
          .receipt-container .store-name-under-logo,
          .receipt-container .store-gstin,
          .receipt-container .section-title,
          .receipt-container .products-header,
          .receipt-container .grand-total-label,
          .receipt-container .grand-total-value,
          .receipt-container .savings-box,
          .receipt-container .thank-you,
          .receipt-container .product-offer-box,
          .receipt-container .amount-value {
            font-weight: 900 !important;
          }

          /* Product block sizing in print */
          .receipt-container .products-header { font-size: 13px !important; letter-spacing: 2px !important; }
          .receipt-container .product-name { font-size: 14px !important; }
          .receipt-container .product-row-2 { font-size: 12px !important; font-weight: 800 !important; }
          .receipt-container .product-row-3 { font-size: 12px !important; font-weight: 800 !important; }
          .receipt-container .product-offer-box { font-size: 11px !important; font-weight: 900 !important; }

          .receipt-container .grand-total-value {
            font-size: 22px !important;
          }
          .logo-img { height: 12mm !important; max-width: 45mm !important; }
          .store-name { font-size: 18px !important; }
          .store-name-under-logo { font-size: 14px !important; }
          .savings-box { font-size: 13px !important; }

          /* MRP block in print */
          .mrp-summary-box { background: transparent !important; padding: 4px 6px !important; }
          .mrp-row { font-size: 12px !important; font-weight: 800 !important; }
          .mrp-row-savings { font-size: 13px !important; font-weight: 900 !important; }
          
          /* QR Code - Larger and Centered in Print */
          .qr-container {
            text-align: center !important;
            width: 100% !important;
          }
          .qr-code { 
            width: 25mm !important; 
            height: 25mm !important;
            display: block !important;
            margin: 4mm auto !important;
          }
          
          /* Terms in print - Larger Font */
          .bill-terms {
            page-break-inside: avoid !important;
          }
          .terms-title {
            font-size: 13px !important;
            font-weight: 900 !important;
          }
          .terms-content {
            font-size: 12px !important;
            font-weight: 700 !important;
            line-height: 1.5 !important;
          }
          
          .print-hide { display: none !important; }
          @page {
            size: 80mm auto;
            margin: 0;
            margin-left: 0;
          }
        }
      `}</style>
    </div>
  );
});

Receipt.displayName = 'Receipt';

export default Receipt;
